import { useEffect, useState } from 'react';
import {
  buildHeadersWithWidgetToken,
  useWidgetTokenStore
} from '@elfsight/lib-utils';
import { useFetch, useStorage } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { DATA_SERVICE_URL } from '../../../constants';
import type {
  GoogleSheetsContent,
  GoogleSheetsContentValue
} from '../../../types';
import {
  type CustomTranslationsMap,
  parseGoogleSheetsData
} from './parse-google-sheets-data';

const CACHE_KEY_PREFIX = 'custom_translations';
const CACHE_DURATION_SECONDS = 60 * 60; // 60 минут в секундах

type CustomTranslationsStatus = 'loading' | 'ready' | 'error';

export function useCustomTranslations(currentLanguage?: string) {
  const {
    editMode,
    integrationGoogleSheets,
    integrationGoogleSheetsId,
    integrationGoogleSheetsAuth
  } = useSettings();

  const [customTranslations, setCustomTranslations] =
    useState<CustomTranslationsMap>();

  const widgetTokenStore = useWidgetTokenStore();

  const spreadsheetId = integrationGoogleSheetsId || '';
  const connectionPid = editMode
    ? integrationGoogleSheetsAuth?.identity
    : undefined;

  const [, cacheOperations] = useStorage<GoogleSheetsContentValue[][]>(
    `${CACHE_KEY_PREFIX}_${spreadsheetId}`,
    []
  );

  const [
    { data: sheetsDataResponse, isLoading: isFetching, isError: isFetchError },
    fetchSheets
  ] = useFetch<GoogleSheetsContent>();
  const [status, setStatus] = useState<CustomTranslationsStatus>(
    integrationGoogleSheets ? 'loading' : 'ready'
  );

  const sheetsData = sheetsDataResponse?.payload?.data;

  useEffect(() => {
    // В конфигураторе нужен connectionPid для доступа к таблице
    const shouldResetTranslations =
      !integrationGoogleSheets ||
      !spreadsheetId?.trim() ||
      (editMode && !connectionPid);

    if (shouldResetTranslations) {
      setCustomTranslations(undefined);
      return;
    }

    if (isFetching) {
      return;
    }

    const url = new URL(
      `${DATA_SERVICE_URL}/google-drive/spreadsheets/${spreadsheetId}/content`
    );
    url.searchParams.set('perPage', '1000');
    url.searchParams.set('page', '1');
    if (connectionPid) {
      url.searchParams.set('connectionPid', connectionPid);
    }
    const widgetToken = widgetTokenStore.get();

    // В режиме конфигуратора - пропускаем кеш и всегда загружаем свежие данные
    if (editMode) {
      fetchSheets({
        url: url.toString(),
        options: {
          headers: buildHeadersWithWidgetToken(widgetToken)
        }
      });
      return;
    }

    // Проверяем кеш с автоматической проверкой TTL
    if (cacheOperations) {
      const cachedData = cacheOperations.get(CACHE_DURATION_SECONDS);

      if (cachedData) {
        try {
          setStatus('loading');
          const parsedTranslations = parseGoogleSheetsData(cachedData);
          setCustomTranslations(parsedTranslations);
          setStatus('ready');
          return;
        } catch {
          cacheOperations.remove();
          setStatus('error');
        }
      }
    }

    const urlWithoutConnectionPid = new URL(url);
    urlWithoutConnectionPid.searchParams.delete('connectionPid');
    fetchSheets({
      url: urlWithoutConnectionPid.toString(),
      options: {
        headers: buildHeadersWithWidgetToken(widgetToken)
      }
    });
  }, [
    integrationGoogleSheets,
    spreadsheetId,
    connectionPid,
    editMode,
    currentLanguage
  ]);

  useEffect(() => {
    if (isFetchError) {
      setCustomTranslations(undefined);
      setStatus('error');
      return;
    }

    if (isFetching || !sheetsData?.length) {
      return;
    }

    try {
      // Сохраняем в кеш с автоматическим timestamp (только в продакшне)
      if (!editMode && cacheOperations) {
        cacheOperations.set(sheetsData);
      }

      const parsedTranslations = parseGoogleSheetsData(sheetsData);
      setCustomTranslations(parsedTranslations);
      setStatus('ready');
    } catch {
      setCustomTranslations(undefined);
      setStatus('error');
    }
  }, [isFetching, isFetchError, sheetsData, editMode, cacheOperations]);

  return { customTranslations, status };
}
